<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import { twMerge } from 'tailwind-merge';
  import { Icon } from '@shared/ui/icon';
  import { clickOutside } from '@shared/lib';
  import type { FieldState } from '@shared/lib/types';
  import { Chip } from '@shared/ui/chip/ui';
  import type { Option } from '../types';
  import { is } from '@shared/lib/utils';
  import { Field } from '@shared/ui/fields/field';

  const dispatcher = createEventDispatcher();

  export let options: Option[] = [];
  export let selectedOption: Option | undefined = undefined;
  export let name = '';
  export let label: string | undefined = undefined;
  export let isMulti = false;
  export let isNoBorder = false;

  export let state: FieldState = 'default';
  export let helperText = '';
  export let containerClass = '';
  export let fieldClass = '';
  export let isMargin = false;

  let isOpen = false;
  let select: HTMLElement;
  export let selectedOptions: Option[] = [];

  function onSelectHandler(option: Option) {
    if (isMulti) {
      const inSelectedIndex = selectedOptions.findIndex((s) => s.value === option.value);
      if (inSelectedIndex > -1) {
        removeFromSelected(inSelectedIndex);
      } else {
        selectedOptions = [...selectedOptions, option];
      }
    } else {
      selectedOption = option;
      isOpen = false;
      dispatcher('change', selectedOption);
    }

    state === 'error' && (state = 'default') && (helperText = '');
  }

  function removeFromSelected(index: number) {
    selectedOptions = [...selectedOptions.slice(0, index), ...selectedOptions.slice(index + 1)];
  }
</script>

<div
  class="relative"
  bind:this="{select}"
  use:clickOutside="{!isOpen}"
  on:clickOutsideAction="{() => (isOpen = false)}"
>
  <input type="hidden" {name} value="{selectedOption?.value || ''}" />

  <Field noMargin="{!isMargin}" noBorder="{isNoBorder}" {label} {state} {helperText} {containerClass}>
    <div
      class="{twMerge(
        'text-default-500 relative flex cursor-pointer items-center justify-between px-4 py-3 pr-8 outline-none',
        fieldClass,
      )}"
      on:keydown="{(e) => e.key === 'Enter' && (isOpen = !isOpen)}"
      on:click="{() => (isOpen = !isOpen)}"
      on:focus="{() => state === 'error' || state === 'success' || (state = 'active')}"
      on:blur="{() => state === 'error' || state === 'success' || (state = 'default')}"
      role="button"
      tabindex="0"
    >
      {#if !selectedOptions.length && !selectedOption?.text}
        <span class="text-inherit dark:text-white">Select</span>
      {:else if isMulti}
        <div class="-my-1 -ml-2 flex flex-wrap gap-2 px-2">
          {#each selectedOptions as option, index}
            <Chip label="{option.text}" onDelete="{() => removeFromSelected(index)}" />
          {/each}
        </div>
      {:else}
        <span class="dark:text-white">{selectedOption?.text}</span>
      {/if}
      <div class="flex h-6 items-center self-baseline">
        <Icon
          name="carrot"
          classes="fill-neutral-900 dark:fill-white {is(isOpen, 'rotate-180')} absolute right-3 transition-transform"
        />
      </div>
    </div>
  </Field>

  <div
    class="absolute bottom-0 left-0 z-40 w-full translate-y-full overflow-hidden transition-all duration-300 {is(
      isOpen,
      'opacity-1q pointer-events-auto',
      'opacity-0q pointer-events-none',
    )} {is(!isOpen, 'invisible-delay invisible')} {is(isMargin, 'bottom-6')}"
  >
    <ul
      class="ul dark:bg-dark-320/70 h-fit overflow-y-auto rounded-lg bg-white/70 py-2 backdrop-blur-xl transition-transform duration-300 dark:backdrop-blur-xl {is(
        !isNoBorder,
        'border-default-300 dark:border-dark-400 border-x border-b border-t-0',
      )} {is(!isOpen, '-translate-y-full scale-y-0')}"
    >
      {#each options as option}
        {@const isSelected = selectedOption?.value === option?.value || selectedOptions.includes(option)}
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <li
          class="dark:text-default-50 flex w-full cursor-pointer items-center justify-between transition-colors {is(
            isSelected,
            'text-default-900',
            'text-default-500',
          )} hover:text-default-900 dark:hover:text-default-300"
          on:click="{() => onSelectHandler(option)}"
          role="option"
          aria-selected="{isSelected}"
        >
          <span class="px-4 py-2.5">{option.text}</span>
          {#if isSelected}
            <span class="pl-3 pr-4">
              <Icon name="checkMark" classes="fill-transparent stroke-default-900 dark:stroke-white" />
            </span>
          {/if}
        </li>
      {/each}
    </ul>
  </div>
</div>

<style>
  .invisible-delay {
    transition: visibility 0s 0.2s;
  }
</style>
